import { inject, Injectable } from '@angular/core';
import { NetworkHelper } from '../helpers';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SHARED_ENV } from '../tokens';
import { CurrencyCacheService } from './currency-cache.service';

dayjs.extend(utc);

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  private readonly networkHelper = inject(NetworkHelper);
  private readonly env = inject(SHARED_ENV);
  private readonly currencyCache = inject(CurrencyCacheService);

  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  getCurrencies(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/currencies`);
  }

  getSymbols(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/symbols`);
  }

  public getWalletTransactions(
    currency: string,
    page: number,
    itemsPerPage: number,
    startDate: number,
    endDate: number,
    type: string,
    status: string,
    userId: string | null,
    includePending: boolean,
    containTotals: boolean,
    asManager?: boolean,
    keyword?: string
  ): Observable<any> {
    const data: any = {
      status: status,
      page: page,
      itemsPerPage: itemsPerPage,
      startDate: startDate,
      endDate: endDate,
    };

    if (userId) {
      data['userId'] = userId;
    }

    if (type) {
      data['type'] = type;
    }

    if (status) {
      data['status'] = status;
    }

    if (currency !== 'any') {
      data['currency'] = currency;
    }

    if (includePending) {
      data['includePending'] = true;
    }

    if (containTotals) {
      data['containTotals'] = true;
    }

    if (asManager) {
      data['asManager'] = true;
    }

    if (keyword?.trim()) {
      data['keyword'] = keyword.trim();
    }

    return this.networkHelper.get(`${this.hostUrl}/user/transactions`, data);
  }

  getWalletTransactionsCsv(
    currency: string,
    startDate: number,
    endDate: number,
    type: string,
    userId: string | null,
    asManager = false
  ): Observable<any> {
    const data: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (userId) {
      data['userId'] = userId;
    }

    if (asManager) {
      data['asManager'] = true;
    }

    if (type) {
      data['type'] = type;
    }

    if (currency !== 'any') {
      data['currency'] = currency;
    }

    data['timeZoneShift'] = dayjs().utcOffset();

    return this.networkHelper.get(
      `${this.hostUrl}/user/csv/transactions`,
      data
    );
  }

  addCurrency(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/add-currency`, data).pipe(
      tap(() => this.currencyCache.loadCurrencies())
    );
  }

  getAllCurrencies(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/currencies`);
  }

  getAllSymbols(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/pair`);
  }

  addSymbol(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/add-pair`, data);
  }

  updateChildCurrenciesFees(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/update-fee-for-children`, data).pipe(
      tap(() => this.currencyCache.loadCurrencies())
    );
  }

  getExternalCurrencyFees(): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/external-currency-fees`
    );
  }

  getEstimatedPrices(currency: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/estimated-prices/${currency}`,
      {}
    );
  }
}
